import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, PenTool, Leaf, Quote } from "lucide-react";
import { api } from "@/lib/api";
import { ASSETS } from "@/lib/assets";
import { useSeo } from "@/lib/seo";
import { useCart } from "@/context/CartContext";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import ProductCard from "@/components/ProductCard";
import { ProductDetail } from "@/pages/Shop";

const HERO_SLIDES = [
  {
    tag: "Now Crafting For You",
    title: ["HAND", "CRAFTED"],
    copy: "Timeless pieces made by hand in Memphis. Custom apparel, tumblers, resin art & personalized gifts — crafted for every occasion.",
    cta: "View The Collection", to: "/shop", image: ASSETS.nHero1,
  },
  {
    tag: "Custom Apparel",
    title: ["WEAR YOUR", "STORY"],
    copy: "Your idea, our craft. Premium prints and bespoke apparel designed to express exactly who you are.",
    cta: "Shop Apparel", to: "/shop", image: ASSETS.nHero2,
  },
  {
    tag: "Personalized Gifts",
    title: ["MADE TO", "GIFT"],
    copy: "Thoughtful, one-of-a-kind gifts personalized for the people who matter most.",
    cta: "Explore Gifts", to: "/shop", image: ASSETS.nHero3,
  },
];

const TAB_CATEGORIES = ["Custom Apparel", "Tumblers & Drinkware", "Personalized Gifts", "Resin Art"];

const MARQUEE_1 = ["Handmade With Heart", "Crafted Just For You", "Small-Batch & Personal", "Made In Memphis, TN"];
const MARQUEE_2 = ["Memphis, TN", "Custom Order Studio", "Bespoke Printing", "Shipped Nationwide"];

const TESTIMONIALS = [
  { quote: "Every piece feels like it was made just for me — the quality and detail are unreal. My go-to for gifts now.", name: "Jasmine R.", role: "Memphis, TN", avatar: ASSETS.nAvatar1 },
  { quote: "They took my rough idea and turned it into apparel my whole team loved. Fast, personal, and beautifully made.", name: "Danielle M.", role: "Team Order", avatar: ASSETS.nAvatar2 },
  { quote: "The resin coasters are stunning. You can tell these are handmade with real care — nothing mass-produced here.", name: "Alexis T.", role: "Repeat Customer", avatar: ASSETS.nAvatar3 },
];

const JOURNAL = [
  { cat: "Behind The Craft", title: "How a Custom Order Comes to Life, Step by Step", date: "Jun 2026", image: ASSETS.nJournal2 },
  { cat: "Resin Art", title: "Caring for Your Handmade Resin Pieces", date: "Jun 2026", image: ASSETS.nJournal1 },
  { cat: "Gift Guide", title: "Personalized Gifts for Every Occasion", date: "Jun 2026", image: ASSETS.nJournal3 },
];

function Marquee({ items, testid }) {
  const row = [...items, ...items];
  return (
    <div className="bg-navy border-y border-white/5 py-5 overflow-hidden marquee-pause" data-testid={testid}>
      <div className="marquee-track animate-marquee">
        {row.map((t, i) => (
          <span key={i} className="inline-flex items-center text-sm sm:text-base uppercase tracking-[0.3em] text-chrome/70 whitespace-nowrap">
            <span className="mx-8">{t}</span>
            <span className="text-electric">✦</span>
          </span>
        ))}
      </div>
    </div>
  );
}

function SectionHead({ eyebrow, title, to, cta = "View all", testid }) {
  return (
    <div className="flex items-end justify-between gap-4 mb-10" data-testid={testid}>
      <div>
        <div className="text-[11px] tracking-[0.3em] uppercase text-electric font-semibold">{eyebrow}</div>
        <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-chrome-bright">{title}</h2>
      </div>
      {to && (
        <Link to={to} className="hidden sm:inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-chrome/70 hover:text-electric transition-colors">
          {cta} <ArrowUpRight size={14} />
        </Link>
      )}
    </div>
  );
}

export default function Home() {
  useSeo(
    "Collins Crafty Creations — Handmade Printing & Custom Apparel | Memphis, TN",
    "Collins Crafty Creations crafts custom apparel, tumblers, personalized gifts, resin art, and made-to-order printing. Shop the collection or start a custom order."
  );
  const [products, setProducts] = useState([]);
  const [slide, setSlide] = useState(0);
  const [tab, setTab] = useState(TAB_CATEGORIES[0]);
  const [detail, setDetail] = useState(null);
  const { add } = useCart();

  useEffect(() => {
    api.get("/products").then(({ data }) => setProducts(data)).catch(() => {});
  }, []);
  useEffect(() => {
    const t = setInterval(() => setSlide((s) => (s + 1) % HERO_SLIDES.length), 6000);
    return () => clearInterval(t);
  }, []);

  const tabs = useMemo(() => TAB_CATEGORIES.filter((c) => products.some((p) => p.category === c)), [products]);
  useEffect(() => { if (tabs.length && !tabs.includes(tab)) setTab(tabs[0]); }, [tabs]); // eslint-disable-line

  const tabProducts = products.filter((p) => p.category === tab).slice(0, 8);
  const featured = (products.filter((p) => p.best_seller || p.featured).length
    ? products.filter((p) => p.best_seller || p.featured)
    : products).slice(0, 4);
  const active = HERO_SLIDES[slide];

  return (
    <>
      {/* ===== HERO SLIDER ===== */}
      <section className="relative min-h-[88vh] overflow-hidden bg-navy-dark" data-testid="home-hero">
        {HERO_SLIDES.map((s, i) => (
          <div key={i} className={`absolute inset-0 transition-opacity duration-[1200ms] ${i === slide ? "opacity-100" : "opacity-0"}`} aria-hidden={i !== slide}>
            <img src={s.image} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-navy-dark via-navy-dark/80 to-navy-dark/30" />
            <div className="absolute inset-0 bg-navy-dark/35" />
          </div>
        ))}
        <div className="relative z-10 max-w-7xl mx-auto px-6 min-h-[88vh] flex flex-col justify-center">
          <motion.div key={slide} initial={{ opacity: 0, y: 26 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-2xl">
            <div className="text-[11px] sm:text-xs tracking-[0.34em] uppercase text-electric font-semibold">{active.tag}</div>
            <h1 className="mt-5 font-serif font-semibold uppercase leading-[0.9] tracking-tight">
              <span className="block text-6xl sm:text-7xl xl:text-8xl metal-text">{active.title[0]}</span>
              <span className="block text-6xl sm:text-7xl xl:text-8xl metal-text">{active.title[1]}</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-chrome/80 leading-relaxed max-w-xl">{active.copy}</p>
            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <Link to={active.to} data-testid="hero-shop"
                className="group inline-flex items-center justify-center gap-2 rounded-lg bg-electric text-white px-9 py-4 text-xs font-bold uppercase tracking-[0.2em] hover:bg-royal transition-colors glow-blue">
                {active.cta} <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
              </Link>
              <Link to="/shop/studio" data-testid="hero-studio"
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-chrome/30 bg-white/5 backdrop-blur-sm text-chrome-bright px-9 py-4 text-xs font-bold uppercase tracking-[0.2em] hover:bg-white/10 hover:border-electric transition-colors">
                Start a Custom Order
              </Link>
            </div>
          </motion.div>
          <div className="absolute bottom-10 left-6 flex items-center gap-2" data-testid="hero-dots">
            {HERO_SLIDES.map((_, i) => (
              <button key={i} onClick={() => setSlide(i)} aria-label={`Slide ${i + 1}`}
                className={`h-1.5 rounded-full transition-all ${i === slide ? "w-8 bg-electric" : "w-3 bg-white/30 hover:bg-white/60"}`} />
            ))}
          </div>
        </div>
      </section>

      <Marquee items={MARQUEE_1} testid="home-marquee-1" />

      {/* ===== TABBED COLLECTION GRID ===== */}
      <section className="bg-navy-dark py-24" data-testid="home-collections">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center">
            <div className="text-[11px] tracking-[0.3em] uppercase text-electric font-semibold">Shop The Craft</div>
            <h2 className="mt-3 font-serif text-4xl sm:text-5xl text-chrome-bright">Redefine Your Everyday</h2>
          </div>
          <div className="mt-10 flex flex-wrap justify-center gap-6 sm:gap-12" data-testid="collection-tabs">
            {tabs.map((c) => (
              <button key={c} onClick={() => setTab(c)} data-testid={`tab-${c}`}
                className={`uppercase tracking-[0.18em] text-xs sm:text-sm pb-2 border-b-2 transition-all ${tab === c ? "border-electric text-chrome-bright" : "border-transparent text-chrome/55 hover:text-chrome"}`}>
                {c}
              </button>
            ))}
          </div>
          <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-x-5 gap-y-10" data-testid="collection-grid">
            {tabProducts.map((p) => <ProductCard key={p.id} product={p} onView={() => setDetail(p)} />)}
          </div>
          <div className="mt-12 text-center">
            <Link to="/shop" className="inline-flex items-center gap-2 rounded-lg border border-chrome/30 text-chrome-bright px-9 py-4 text-xs font-bold uppercase tracking-[0.2em] hover:border-electric hover:text-electric transition-colors" data-testid="collections-all">
              Shop All Products <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* ===== CATEGORY BANNERS ===== */}
      <section className="bg-navy-dark pb-24" data-testid="home-banners">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-5">
          {[
            { title: "Custom Apparel", copy: "Premium prints & bespoke pieces, made to express.", image: ASSETS.nApparel },
            { title: "Personalized Gifts", copy: "One-of-a-kind keepsakes for every occasion.", image: ASSETS.nGifts },
          ].map((b, i) => (
            <Link key={b.title} to="/shop" data-testid={`banner-${i}`}
              className="group relative overflow-hidden rounded-2xl aspect-[3/4] md:aspect-[4/3] ring-1 ring-white/10">
              <img src={b.image} alt={b.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
              <div className="absolute inset-0 bg-navy-dark/45 group-hover:bg-navy-dark/55 transition-colors" />
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-8">
                <h3 className="font-serif text-4xl md:text-5xl uppercase tracking-[0.1em] text-white">{b.title}</h3>
                <p className="mt-3 text-sm text-chrome/80 max-w-xs">{b.copy}</p>
                <span className="mt-6 inline-flex items-center gap-2 border border-white/60 text-white uppercase tracking-[0.2em] text-[11px] font-bold px-7 py-3 rounded-md group-hover:bg-white group-hover:text-navy-dark transition-colors">
                  Shop Now <ArrowRight size={13} />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* ===== PROMO STRIP ===== */}
      <section className="bg-navy/40 border-y border-white/5 py-24 text-center px-6" data-testid="home-promo">
        <p className="font-serif text-3xl md:text-5xl leading-tight text-chrome-bright max-w-4xl mx-auto">
          Crafted with <em className="blue-text not-italic font-semibold">intention</em>, made to <em className="italic">mean something</em> in every season.
        </p>
      </section>

      {/* ===== EDITORIAL "LOOKS" BANNER ===== */}
      <section className="relative h-[70vh] min-h-[420px] overflow-hidden" data-testid="home-looks">
        <img src={ASSETS.nLooks} alt="Handmade pieces you'll love" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-navy-dark/55" />
        <div className="relative h-full flex flex-col items-center justify-center text-center px-6">
          <h2 className="font-serif text-4xl md:text-6xl text-white uppercase tracking-[0.06em]">The Pieces You'll Love</h2>
          <p className="mt-4 text-chrome/85 max-w-xl">Thoughtful design, refined materials, and handmade details made to last beyond the moment.</p>
          <Link to="/shop" className="mt-8 inline-flex items-center gap-2 bg-electric text-white uppercase tracking-[0.2em] text-xs font-bold px-9 py-4 rounded-lg hover:bg-royal transition-colors glow-blue" data-testid="looks-cta">
            Explore The Shop <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      {/* ===== FEATURED PRODUCTS ===== */}
      {featured.length > 0 && (
        <section className="bg-navy-dark py-24" data-testid="home-featured">
          <div className="max-w-7xl mx-auto px-6">
            <SectionHead eyebrow="Best Sellers" title="Crowd Favorites" to="/shop" cta="Shop all" testid="featured-head" />
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-5 gap-y-10" data-testid="featured-grid">
              {featured.map((p) => <ProductCard key={p.id} product={p} onView={() => setDetail(p)} />)}
            </div>
          </div>
        </section>
      )}

      {/* ===== TESTIMONIALS ===== */}
      <section className="bg-navy/40 border-y border-white/5 py-24" data-testid="home-testimonials">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <Quote size={30} className="text-electric mx-auto" />
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl text-chrome-bright">Loved by Memphis & Beyond</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                className="rounded-2xl bg-white/[0.03] border border-white/10 p-8 flex flex-col" data-testid={`testimonial-${i}`}>
                <p className="font-serif italic text-lg text-chrome-bright leading-relaxed flex-grow">“{t.quote}”</p>
                <div className="mt-6 flex items-center gap-3">
                  <img src={t.avatar} alt={t.name} className="w-11 h-11 rounded-full object-cover ring-1 ring-electric/30" />
                  <div>
                    <div className="text-sm font-semibold text-chrome-bright">{t.name}</div>
                    <div className="text-[11px] uppercase tracking-wide text-chrome/50">{t.role}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== BRAND STORY ===== */}
      <section className="bg-navy-dark py-24 overflow-hidden" data-testid="home-story">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-[11px] tracking-[0.3em] uppercase text-electric font-semibold flex items-center gap-2"><Leaf size={14} /> Small-Batch & Personal</div>
            <h2 className="mt-5 font-serif text-5xl md:text-6xl uppercase leading-[0.95] text-chrome-bright">
              Made to order.<br /><span className="metal-text">Made to mean<br />something.</span>
            </h2>
            <p className="mt-6 text-chrome/75 leading-relaxed max-w-md">
              Nothing here is mass-produced. Every piece is crafted by hand in Memphis — designed with you, made with care, and finished to last. Bring us your vision and we'll make it real.
            </p>
            <Link to="/shop/studio" data-testid="story-cta"
              className="group mt-8 inline-flex items-center gap-2 rounded-lg bg-electric text-white px-9 py-4 text-xs font-bold uppercase tracking-[0.2em] hover:bg-royal transition-colors glow-blue">
              <PenTool size={14} /> Start Your Custom Order <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-5">
            <div className="rounded-2xl overflow-hidden ring-1 ring-white/10 aspect-[3/4] mt-10">
              <img src={ASSETS.nStoryA} alt="Artisan crafting by hand" className="w-full h-full object-cover" />
            </div>
            <div className="rounded-2xl overflow-hidden ring-1 ring-white/10 aspect-[3/4]">
              <img src={ASSETS.nStoryB} alt="Handmade detail work" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      <Marquee items={MARQUEE_2} testid="home-marquee-2" />

      {/* ===== JOURNAL ===== */}
      <section className="bg-navy-dark py-24" data-testid="home-journal">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHead eyebrow="From The Journal" title="Craft Notes & Ideas" testid="journal-head" />
          <div className="grid md:grid-cols-3 gap-6">
            {JOURNAL.map((j, i) => (
              <Link key={i} to="/shop" data-testid={`journal-${i}`} className="group rounded-2xl overflow-hidden bg-white/[0.03] border border-white/10 hover:border-electric/40 transition-colors">
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={j.image} alt={j.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="p-6">
                  <span className="inline-block text-[10px] uppercase tracking-[0.18em] text-electric font-bold border border-electric/30 rounded-full px-3 py-1">{j.cat}</span>
                  <h3 className="mt-4 font-serif text-xl text-chrome-bright leading-snug group-hover:text-electric transition-colors">{j.title}</h3>
                  <div className="mt-3 text-[11px] uppercase tracking-wide text-chrome/45">Collins Crafty · {j.date}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Shared product detail dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-3xl bg-white p-0 max-h-[92vh] overflow-y-auto">
          {detail && <ProductDetail product={detail} onAdd={add} onClose={() => setDetail(null)} />}
        </DialogContent>
      </Dialog>
    </>
  );
}
